import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Alert, AlertDescription } from "./ui/alert";
import { Download, AlertTriangle, Loader2, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001/api";

const ExportLeadsDialog = ({ 
  open, 
  onClose, 
  selectedIds = [], 
  filters = {},
  totalFilteredCount = 0 
}) => {
  const [mode, setMode] = useState("filtered"); // 'filtered' or 'manual'
  const [includeAi, setIncludeAi] = useState(true);
  const [includeManual, setIncludeManual] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [showLargeConfirm, setShowLargeConfirm] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Update export count based on mode
    if (mode === "manual") {
      setExportCount(selectedIds.length);
    } else {
      setExportCount(totalFilteredCount);
    }
  }, [mode, selectedIds, totalFilteredCount]);

  useEffect(() => {
    // Poll job status if we have a jobId
    if (jobId && jobStatus !== "completed" && jobStatus !== "failed") {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${API}/api/admin/exports/leads/${jobId}/status`,
            { withCredentials: true }
          );
          setJobStatus(response.data.status);
          setProgress(response.data.progress || 0);

          if (response.data.status === "completed") {
            clearInterval(interval);
            handleJobDownload(jobId);
          } else if (response.data.status === "failed") {
            clearInterval(interval);
            toast.error("Export failed. Please try again.");
            setIsExporting(false);
          }
        } catch (error) {
          clearInterval(interval);
          console.error("Error polling job status:", error);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [jobId, jobStatus]);

  const handleJobDownload = async (id) => {
    try {
      const response = await axios.get(
        `${API}/api/admin/exports/leads/${id}/download`,
        {
          withCredentials: true,
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `leads_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export downloaded successfully!");
      setIsExporting(false);
      onClose();
    } catch (error) {
      toast.error("Failed to download export");
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    // Check if count is valid
    if (exportCount === 0) {
      toast.error("No leads selected for export");
      return;
    }

    // Show confirmation for large exports
    if (exportCount > 20000 && !showLargeConfirm) {
      setShowLargeConfirm(true);
      return;
    }

    setIsExporting(true);
    setShowLargeConfirm(false);

    try {
      const exportRequest = {
        mode,
        filters: mode === "filtered" ? filters : {},
        selectedIds: mode === "manual" ? selectedIds : [],
        includeAi,
        includeManual,
      };

      toast.info("Export started...");

      const response = await axios.post(
        `${API}/api/admin/exports/leads`,
        exportRequest,
        {
          withCredentials: true,
          responseType: exportCount < 5000 ? "blob" : "json",
        }
      );

      // Handle direct download for small exports
      if (exportCount < 5000) {
        // Check if response is actually a blob (not an error JSON)
        if (response.data instanceof Blob) {
          // Check if blob is actually CSV or an error
          if (response.data.type.includes('json')) {
            // It's an error response as JSON blob
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.detail || 'Export failed');
          }

          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = url;
          
          // Extract filename from headers or use default
          const contentDisposition = response.headers['content-disposition'];
          let filename = `leads_export_${new Date().toISOString().split("T")[0]}.csv`;
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }
          
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          toast.success(`Exported ${exportCount} leads successfully!`);
          setIsExporting(false);
          onClose();
        } else {
          throw new Error('Unexpected response format');
        }
      } else {
        // Handle async job for large exports
        setJobId(response.data.jobId);
        setJobStatus(response.data.status);
        toast.info(`Large export queued. Processing ${response.data.estimatedRows} rows...`);
      }
    } catch (error) {
      console.error("Export error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to export leads";
      toast.error(errorMsg);
      setIsExporting(false);
    }
  };

  const canExport = exportCount > 0 && (includeAi || includeManual);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Leads to CSV
          </DialogTitle>
          <DialogDescription>
            Download lead data with manual input and AI-enriched fields
          </DialogDescription>
        </DialogHeader>

        {!showLargeConfirm && !jobId && (
          <div className="space-y-4 py-4">
            {/* Selection Mode */}
            <div className="space-y-3">
              <Label>Selection Mode</Label>
              <RadioGroup value={mode} onValueChange={setMode}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="filtered" id="filtered" />
                  <Label htmlFor="filtered" className="font-normal cursor-pointer">
                    Export all filtered leads ({totalFilteredCount} leads)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="manual" 
                    id="manual"
                    disabled={selectedIds.length === 0}
                  />
                  <Label 
                    htmlFor="manual" 
                    className={`font-normal ${selectedIds.length === 0 ? 'text-slate-400' : 'cursor-pointer'}`}
                  >
                    Export selected leads only ({selectedIds.length} selected)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Data Options */}
            <div className="space-y-3">
              <Label>Include Data</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-manual"
                    checked={includeManual}
                    onCheckedChange={setIncludeManual}
                  />
                  <Label htmlFor="include-manual" className="font-normal cursor-pointer">
                    Manual input fields (name, email, phone, company, etc.)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-ai"
                    checked={includeAi}
                    onCheckedChange={setIncludeAi}
                  />
                  <Label htmlFor="include-ai" className="font-normal cursor-pointer">
                    AI-enriched fields (company analysis, qualification, insights)
                  </Label>
                </div>
              </div>
            </div>

            {/* Export Preview */}
            <Alert>
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span className="text-sm">
                    <strong>{exportCount}</strong> lead{exportCount !== 1 ? "s" : ""} will be exported
                  </span>
                  {exportCount > 20000 && (
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  )}
                </div>
              </AlertDescription>
            </Alert>

            {!canExport && (
              <Alert variant="destructive">
                <AlertDescription className="text-sm">
                  Please select at least one data type to include
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Large Export Confirmation */}
        {showLargeConfirm && (
          <div className="space-y-4 py-4">
            <Alert>
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription>
                <p className="font-semibold mb-2">Large Export ({exportCount.toLocaleString()} rows)</p>
                <p className="text-sm">
                  This export contains a large number of leads and will be processed in the background.
                  You'll be able to download the file once it's ready.
                </p>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Job Progress */}
        {jobId && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Export Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-2">
                {jobStatus === "completed" ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Export completed! Downloading...
                  </>
                ) : (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing {exportCount.toLocaleString()} leads...
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!jobId && (
            <>
              <Button variant="outline" onClick={onClose} disabled={isExporting}>
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                disabled={!canExport || isExporting}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : showLargeConfirm ? (
                  "Confirm Export"
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </>
                )}
              </Button>
            </>
          )}
          {jobId && jobStatus !== "completed" && (
            <Button variant="outline" onClick={onClose}>
              Close (Export continues in background)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportLeadsDialog;
